/**
 * Shared API client for admin application
 * Provides centralized authentication, error handling, and request management
 */

/**
 * Storage key for admin JWT token
 * Must match the key used in AdminAuthContext
 */
export const ADMIN_TOKEN_STORAGE_KEY = 'sparkadmin_token';

/**
 * Base API URL from environment or default
 */
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

/**
 * Standard API error response format
 */
export interface ApiErrorResponse {
  success: false;
  error: string | string[];
  message?: string;
  timestamp: string;
}

export function formatApiError(error: string | string[], status: number): string {
  if (Array.isArray(error)) return error.join(' · ');
  return error || `Request failed with status ${status}`;
}

/**
 * Error thrown for non-2xx responses. Keeps the status and the individual
 * error entries (e.g. "<path>: <message>" validation errors) for field-level display.
 */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly errors: string[]) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Options for API requests
 */
export interface ApiRequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  headers?: HeadersInit;
  requireAuth?: boolean; // Default: true
}

/**
 * Get stored admin JWT token
 * @returns JWT token or null if not authenticated
 */
export function getAdminToken(): string | null {
  return localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);
}

/**
 * Make an authenticated API request
 * 
 * @template T - Expected response data type
 * @param endpoint - API endpoint (without base URL, e.g., '/admin/spark-activities')
 * @param options - Request configuration options
 * @returns Promise resolving to typed response data
 * @throws Error if request fails or authentication is required but missing
 * 
 * @example
 * ```typescript
 * const activity = await apiRequest<SparkActivity>('/admin/spark-activities/123');
 * 
 * const created = await apiRequest<SparkActivity>('/admin/spark-activities', {
 *   method: 'POST',
 *   body: { title: 'New Activity' }
 * });
 * ```
 */
export async function apiRequest<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    requireAuth = true,
  } = options;

  // Get authentication token
  const token = getAdminToken();
  
  if (requireAuth && !token) {
    throw new Error('Not authenticated');
  }

  // Build headers
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge custom headers
  if (headers) {
    Object.entries(headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        requestHeaders[key] = value;
      }
    });
  }

  // Add authorization header if token exists
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Build request configuration
  const requestConfig: RequestInit = {
    method,
    headers: requestHeaders,
  };

  // Add body for non-GET requests
  if (body && method !== 'GET') {
    requestConfig.body = JSON.stringify(body);
  }

  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${API_BASE_URL}${normalizedEndpoint}`;

  // Make the request
  const response = await fetch(url, requestConfig);

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }
    throw new Error('Invalid response from server');
  }

  // Handle error responses
  if (!response.ok) {
    const errorData = data as ApiErrorResponse;
    const errors = Array.isArray(errorData.error) ? errorData.error : errorData.error ? [errorData.error] : [];
    throw new ApiError(
      formatApiError(errorData.error, response.status) || `Request failed with status ${response.status}`,
      response.status,
      errors,
    );
  }

  return data as T;
}

/**
 * Authenticated GET that saves the response body as a file.
 * Errors keep the server's message, same as apiRequest.
 */
export async function apiDownload(endpoint: string, fallbackFilename: string): Promise<void> {
  const token = getAdminToken();
  if (!token) throw new Error('Not authenticated');

  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const response = await fetch(`${API_BASE_URL}${normalizedEndpoint}`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const errorData = (await response.json().catch(() => null)) as ApiErrorResponse | null;
    const errors = errorData ? (Array.isArray(errorData.error) ? errorData.error : [errorData.error]) : [];
    throw new ApiError(
      errorData ? formatApiError(errorData.error, response.status) : `Request failed with status ${response.status}`,
      response.status,
      errors,
    );
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fallbackFilename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/**
 * Legacy wrapper for backward compatibility with existing authenticatedFetch calls
 * @deprecated Use apiRequest instead
 */
export async function authenticatedFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAdminToken();
  
  if (!token) {
    throw new Error('Not authenticated');
  }

  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${API_BASE_URL}${normalizedEndpoint}`;
  
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });
}

/**
 * Get authorization headers with JWT token
 * @deprecated Use apiRequest instead
 * @returns Headers object with Authorization
 */
export function getAuthHeaders(): HeadersInit {
  const token = getAdminToken();
  
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
}
